import { type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from "react";
import type { Point } from "./geometry";
import type { LongPressProps } from "./triggerProps";
import { isEditable } from "./triggerTarget";

/** Touch: how long a press must last to open the menu. */
export const LONG_PRESS_MS = 520;
/** How far (px) a finger may drift before the press counts as a scroll or a drag. */
const LONG_PRESS_TOLERANCE_PX = 10;
/** Android fires `contextmenu` for the same long-press; it is ignored for this long afterwards. */
const ANDROID_CONTEXTMENU_WINDOW_MS = 800;
/** The finger lifting after a long-press must not also "click" what is under it within this window. */
const CLICK_SUPPRESSION_MS = 800;

interface Press {
  x: number;
  y: number;
  target: Element;
  timer: ReturnType<typeof setTimeout>;
}

export interface LongPressOptions<T> {
  delayMs: number;
  /** While true, presses are ignored. */
  disabled: boolean;
  /** Maps the pressed element to what the menu is about; null ignores the press. */
  resolve: (element: Element) => T | null;
  onLongPress: (target: T, point: Point) => void;
}

export interface LongPress {
  /** Forgets a press in progress. */
  cancel: () => void;
  /** True right after a long-press opened the menu, while Android's own `contextmenu` may still arrive. */
  firedRecently: () => boolean;
  props: LongPressProps;
}

/** Opens the menu on a touch long-press that stays in place, and swallows the click that follows. */
export function useLongPress<T>({ delayMs, disabled, resolve, onLongPress }: LongPressOptions<T>): LongPress {
  const press = useRef<Press | null>(null);
  const suppressClickUntil = useRef(0);
  const longPressAt = useRef(0);

  const cancel = useCallback(() => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
  }, []);
  const firedRecently = useCallback(() => Date.now() - longPressAt.current < ANDROID_CONTEXTMENU_WINDOW_MS, []);

  useEffect(() => cancel, [cancel]);

  function fire(point: Point): void {
    const current = press.current;
    press.current = null;
    if (!current) return;
    const target = resolve(current.target);
    if (target === null) return;
    longPressAt.current = Date.now();
    suppressClickUntil.current = Date.now() + CLICK_SUPPRESSION_MS;
    onLongPress(target, point);
  }

  function start(e: ReactPointerEvent<HTMLElement>): void {
    cancel();
    if (e.pointerType !== "touch" || disabled) return;
    const el = e.target as Element;
    if (!e.currentTarget.contains(el) || isEditable(el)) return;
    const point = { x: e.clientX, y: e.clientY };
    press.current = { ...point, target: el, timer: setTimeout(() => fire(point), delayMs) };
  }

  function cancelIfMoved(e: ReactPointerEvent<HTMLElement>): void {
    const p = press.current;
    if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_TOLERANCE_PX) cancel();
  }

  function swallowClickAfterLongPress(e: ReactMouseEvent<HTMLElement>): void {
    if (Date.now() >= suppressClickUntil.current) return;
    e.preventDefault();
    e.stopPropagation();
    suppressClickUntil.current = 0;
  }

  return {
    cancel,
    firedRecently,
    props: {
      onPointerDown: start,
      onPointerMove: cancelIfMoved,
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onClickCapture: swallowClickAfterLongPress,
    },
  };
}
